/**
 * DashboardTodos - Gjøremålsliste for Dashboard
 * Viser alle aktive TODOs på tvers av saker
 * Støtter synkronisering med iPhone Reminders/Calendar
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './DashboardTodos.css';

export default function DashboardTodos() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visNyTodo, setVisNyTodo] = useState(false);
  const [redigerTodo, setRedigerTodo] = useState(null);
  const [nyTodo, setNyTodo] = useState({
    tittel: '',
    beskrivelse: '',
    prioritet: 'middels',
    forfall_dato: null,
    sak_id: null,
    type: 'todo',
    kritiskhet: 'normal'
  });
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [saker, setSaker] = useState([]);

  useEffect(() => {
    lastTodos();
    lastSaker();
  }, []);

  const lastTodos = async () => {
    try {
      setLoading(true);
      const data = await crmApi.request('/api/oppgaver?status=ikke_startet,i_gang');
      const oppgaver = data.oppgaver || [];

      // Sorter: Forfalte oppgaver først, deretter etter prioritet og forfallsdato
      const sorterte = oppgaver.sort((a, b) => {
        const aForfalt = a.forfall_dato && new Date(a.forfall_dato) < new Date();
        const bForfalt = b.forfall_dato && new Date(b.forfall_dato) < new Date();

        // Forfalte først
        if (aForfalt && !bForfalt) return -1;
        if (!aForfalt && bForfalt) return 1;

        // Deretter prioritet
        const prioritetVekt = { urgent: 4, 'høy': 3, 'middels': 2, 'lav': 1, kritisk: 4 };
        const aPri = prioritetVekt[a.prioritet] || 0;
        const bPri = prioritetVekt[b.prioritet] || 0;
        if (aPri !== bPri) return bPri - aPri;

        // Til slutt forfallsdato (nærmeste først)
        if (a.forfall_dato && b.forfall_dato) {
          return new Date(a.forfall_dato) - new Date(b.forfall_dato);
        }
        if (a.forfall_dato) return -1;
        if (b.forfall_dato) return 1;

        return 0;
      });

      setTodos(sorterte);
    } catch (error) {
      console.error('Feil ved lasting av TODOs:', error);
      setTodos([]);
    } finally {
      setLoading(false);
    }
  };

  const lastSaker = async () => {
    try {
      const data = await crmApi.request('/api/saker');
      setSaker(data.saker || []);
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
      setSaker([]);
    }
  };

  const synkroniserTilMicrosoft = async () => {
    if (!window.confirm('Synkroniser alle aktive oppgaver til Microsoft To Do og Kalender?')) {
      return;
    }

    try {
      setSyncing(true);
      setSyncStatus(null);

      const result = await crmApi.request('/api/oppgaver/sync', {
        method: 'POST'
      });

      setSyncStatus({
        type: 'success',
        message: result.message,
        details: result.details
      });

      alert(`✅ ${result.message}\nVellykket: ${result.details.synced}\nFeilet: ${result.details.failed}`);
    } catch (error) {
      console.error('Feil ved synkronisering:', error);
      setSyncStatus({
        type: 'error',
        message: 'Kunne ikke synkronisere oppgaver'
      });
      alert('❌ Kunne ikke synkronisere oppgaver: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const opprettTodo = async (e) => {
    e.preventDefault();

    if (!nyTodo.tittel.trim()) {
      alert('Tittel er påkrevd');
      return;
    }

    try {
      await crmApi.request('/api/oppgaver', {
        method: 'POST',
        body: JSON.stringify(nyTodo)
      });

      alert('✅ Gjøremål opprettet!');
      setVisNyTodo(false);
      setNyTodo({ tittel: '', beskrivelse: '', prioritet: 'middels', forfall_dato: null, sak_id: null, type: 'todo' });
      lastTodos();
    } catch (error) {
      console.error('Feil ved opprettelse:', error);
      alert('Kunne ikke opprette gjøremål');
    }
  };

  const markerFullfort = async (todoId) => {
    try {
      await crmApi.request(`/api/oppgaver/${todoId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'fullfort' })
      });

      lastTodos();
    } catch (error) {
      console.error('Feil ved markering:', error);
      alert('Kunne ikke markere som fullført');
    }
  };

  const oppdaterTodo = async (e) => {
    e.preventDefault();

    if (!redigerTodo.tittel.trim()) {
      alert('Tittel er påkrevd');
      return;
    }

    try {
      await crmApi.request(`/api/oppgaver/${redigerTodo.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          tittel: redigerTodo.tittel,
          beskrivelse: redigerTodo.beskrivelse,
          prioritet: redigerTodo.prioritet,
          forfall_dato: redigerTodo.forfall_dato,
          sak_id: redigerTodo.sak_id || null
        })
      });

      alert('✅ Gjøremål oppdatert!');
      setRedigerTodo(null);
      lastTodos();
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      alert('Kunne ikke oppdatere gjøremål');
    }
  };

  const eksporterTilIPhone = async () => {
    try {
      // Generer iCal-format for iPhone Reminders/Calendar
      const response = await crmApi.request('/api/oppgaver/export/ical');
      const icalData = response.ical;

      // Last ned som .ics fil
      const blob = new Blob([icalData], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'oppgaver.ics';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      alert('✅ Eksportert! Åpne filen på iPhone for å importere til Påminnelser/Kalender');
    } catch (error) {
      console.error('Feil ved eksport:', error);
      alert('Kunne ikke eksportere til iPhone');
    }
  };

  const getPrioritetFarge = (prioritet) => {
    switch (prioritet) {
      case 'urgent': return '#ef4444';
      case 'høy': return '#f97316';
      case 'middels': return '#eab308';
      case 'lav': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatDato = (timestamp) => {
    if (!timestamp) return 'Ingen frist';
    const dato = new Date(timestamp);
    const iDag = new Date();
    const diffDager = Math.ceil((dato - iDag) / (1000 * 60 * 60 * 24));

    if (diffDager < 0) return `⚠️ ${Math.abs(diffDager)} dag${Math.abs(diffDager) !== 1 ? 'er' : ''} forsinket`;
    if (diffDager === 0) return '🔴 I dag';
    if (diffDager === 1) return '🟠 I morgen';
    if (diffDager <= 7) return `🟡 Om ${diffDager} dager`;
    return dato.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getKritiskhetBadge = (kritiskhet) => {
    if (!kritiskhet || kritiskhet === 'normal') return null;

    const styles = {
      absolutt: { bg: '#fee2e2', color: '#991b1b', label: '🔴 ABSOLUTT' },
      viktig: { bg: '#fef3c7', color: '#92400e', label: '🟡 VIKTIG' }
    };

    const style = styles[kritiskhet];
    if (!style) return null;

    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        marginLeft: '6px'
      }}>
        {style.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-todos">
        <h3>✅ Mine gjøremål</h3>
        <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Laster...</p>
      </div>
    );
  }

  // Tell forfalte oppgaver
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today

  const forfalteTodos = todos.filter(t => {
    if (!t.forfall_dato) return false;

    // Håndter både streng-format ('2026-04-11') og timestamp (1776124800000)
    const forfallDato = typeof t.forfall_dato === 'string'
      ? new Date(t.forfall_dato)
      : new Date(Number(t.forfall_dato));

    forfallDato.setHours(0, 0, 0, 0);
    return forfallDato < now;
  });

  return (
    <div className="dashboard-todos">
      <div className="todos-header">
        <div>
          <h3>✅ Mine gjøremål</h3>
          <p className="todos-count">
            {todos.length} aktive oppgaver
            {forfalteTodos.length > 0 && (
              <span style={{color: '#ef4444', marginLeft: '8px', fontWeight: 'bold'}}>
                • {forfalteTodos.length} forfalt{forfalteTodos.length !== 1 ? 'e' : ''}
              </span>
            )}
          </p>
        </div>
        <div className="todos-actions">
          <button className="btn-export" onClick={eksporterTilIPhone} title="Eksporter til iPhone">
            📱 iPhone
          </button>
          <button
            className="btn-sync"
            onClick={synkroniserTilMicrosoft}
            disabled={syncing}
            title="Synkroniser til Microsoft To Do og Kalender"
          >
            {syncing ? '⏳ Synkroniserer...' : '🔄 Microsoft'}
          </button>
          <button className="btn-ny-todo" onClick={() => setVisNyTodo(!visNyTodo)}>
            + Ny
          </button>
        </div>
      </div>

      {/* Varsel om forfalte oppgaver */}
      {forfalteTodos.length > 0 && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{fontSize: '1.5rem'}}>🚨</span>
          <div>
            <strong style={{color: '#991b1b'}}>
              {forfalteTodos.length} oppgave{forfalteTodos.length !== 1 ? 'r' : ''} er forfalt{forfalteTodos.length !== 1 ? 'e' : ''}!
            </strong>
            <p style={{margin: '4px 0 0 0', fontSize: '0.9rem', color: '#7f1d1d'}}>
              Vennligst gjennomgå og oppdater status eller frist.
            </p>
          </div>
        </div>
      )}

      {visNyTodo && (
        <form className="ny-todo-form" onSubmit={opprettTodo}>
          <input
            type="text"
            placeholder="Tittel på gjøremål..."
            value={nyTodo.tittel}
            onChange={(e) => setNyTodo({ ...nyTodo, tittel: e.target.value })}
            required
          />
          <textarea
            placeholder="Beskrivelse (valgfri)"
            value={nyTodo.beskrivelse}
            onChange={(e) => setNyTodo({ ...nyTodo, beskrivelse: e.target.value })}
            rows={2}
          />
          <div className="todo-form-row">
            <select
              value={nyTodo.sak_id || ''}
              onChange={(e) => setNyTodo({ ...nyTodo, sak_id: e.target.value || null })}
              title="Velg sak (valgfritt)"
            >
              <option value="">Ingen sak</option>
              {saker.map(sak => (
                <option key={sak.id} value={sak.id}>
                  {sak.saksnummer} - {sak.tittel || sak.klient_navn}
                </option>
              ))}
            </select>
          </div>
          <div className="todo-form-row">
            <select
              value={nyTodo.prioritet}
              onChange={(e) => setNyTodo({ ...nyTodo, prioritet: e.target.value })}
            >
              <option value="lav">Lav prioritet</option>
              <option value="middels">Middels prioritet</option>
              <option value="høy">Høy prioritet</option>
              <option value="urgent">🔥 Urgent</option>
            </select>
            <input
              type="date"
              value={nyTodo.forfall_dato ? new Date(Number(nyTodo.forfall_dato)).toISOString().split('T')[0] : ''}
              onChange={(e) => setNyTodo({ ...nyTodo, forfall_dato: e.target.value ? new Date(e.target.value).getTime() : null })}
            />
          </div>
          <div className="todo-form-row">
            <select
              value={nyTodo.kritiskhet}
              onChange={(e) => setNyTodo({ ...nyTodo, kritiskhet: e.target.value })}
              title="Kritiskhetsnivå - viktig/absolutt gir timevarsler"
            >
              <option value="normal">⚪ Normal kritiskhet</option>
              <option value="viktig">🟡 Viktig (varsles hver time)</option>
              <option value="absolutt">🔴 Absolutt (kritisk varsling)</option>
            </select>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-lagre">Lagre</button>
            <button type="button" className="btn-avbryt" onClick={() => setVisNyTodo(false)}>Avbryt</button>
          </div>
        </form>
      )}

      {todos.length === 0 ? (
        <div className="todos-empty">
          <p>🎉 Ingen aktive gjøremål!</p>
          <small>Klikk "+ Ny" for å legge til</small>
        </div>
      ) : (
        <>
          {/* Forfalte oppgaver */}
          {forfalteTodos.length > 0 && (
            <div className="todos-seksjon">
              <h4 style={{fontSize: '0.9rem', color: '#991b1b', marginBottom: '8px', fontWeight: 600}}>
                🚨 Forfalte ({forfalteTodos.length})
              </h4>
              <div className="todos-liste">
                {forfalteTodos.map((todo) => (
                  <div
                    key={todo.id}
                    className="todo-item"
                    onClick={() => setRedigerTodo(todo)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: '#fef2f2',
                      borderLeft: '4px solid #ef4444'
                    }}
                  >
                    <input
                      type="checkbox"
                      onChange={() => markerFullfort(todo.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="todo-checkbox"
                    />
                    <div className="todo-content">
                      <h4 style={{ color: '#991b1b' }}>⚠️ {todo.tittel}{getKritiskhetBadge(todo.kritiskhet)}</h4>
                      {todo.beskrivelse && <p className="todo-beskrivelse">{todo.beskrivelse}</p>}
                      <div className="todo-meta">
                        <span className="todo-dato" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                          {formatDato(todo.forfall_dato)}
                        </span>
                        {todo.sak_saksnummer && (
                          <span className="todo-sak">Sak #{todo.sak_saksnummer}</span>
                        )}
                      </div>
                    </div>
                    <span
                      className="todo-prioritet"
                      style={{ backgroundColor: '#ef4444' }}
                      title={todo.prioritet}
                    >
                      🔥
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Møter (inkl. rettsmøter) - i dag og fremover */}
          {(() => {
            const moter = todos.filter(t =>
              !forfalteTodos.includes(t) &&
              (t.type === 'møte' || t.type === 'rettsmøte' || t.type === 'møteforberedelse')
            );

            if (moter.length === 0) return null;

            return (
              <div className="todos-seksjon" style={{marginTop: forfalteTodos.length > 0 ? '20px' : '0'}}>
                <h4 style={{fontSize: '0.9rem', color: '#2563eb', marginBottom: '8px', fontWeight: 600}}>
                  📅 Møter og rettsmøter ({moter.length})
                </h4>
                <div className="todos-liste">
                  {moter.map((todo) => (
                    <div
                      key={todo.id}
                      className="todo-item"
                      onClick={() => setRedigerTodo(todo)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: '#eff6ff',
                        borderLeft: '4px solid #2563eb'
                      }}
                    >
                      <input
                        type="checkbox"
                        onChange={() => markerFullfort(todo.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="todo-checkbox"
                      />
                      <div className="todo-content">
                        <h4 style={{ color: '#1e40af' }}>
                          {todo.type === 'rettsmøte' ? '⚖️' : '👥'} {todo.tittel}{getKritiskhetBadge(todo.kritiskhet)}
                        </h4>
                        {todo.beskrivelse && <p className="todo-beskrivelse">{todo.beskrivelse}</p>}
                        <div className="todo-meta">
                          <span className="todo-dato">{formatDato(todo.forfall_dato)}</span>
                          {todo.sak_saksnummer && (
                            <span className="todo-sak">Sak #{todo.sak_saksnummer}</span>
                          )}
                        </div>
                      </div>
                      <span
                        className="todo-prioritet"
                        style={{ backgroundColor: getPrioritetFarge(todo.prioritet) }}
                        title={todo.prioritet}
                      >
                        {todo.prioritet === 'urgent' ? '🔥' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Øvrige oppgaver - i dag og fremover */}
          {(() => {
            const ovrige = todos.filter(t =>
              !forfalteTodos.includes(t) &&
              t.type !== 'møte' &&
              t.type !== 'rettsmøte' &&
              t.type !== 'møteforberedelse'
            );

            if (ovrige.length === 0) return null;

            return (
              <div className="todos-seksjon" style={{marginTop: (forfalteTodos.length > 0 || todos.some(t => t.type === 'møte' || t.type === 'rettsmøte' || t.type === 'møteforberedelse')) ? '20px' : '0'}}>
                <h4 style={{fontSize: '0.9rem', color: '#6b7280', marginBottom: '8px', fontWeight: 600}}>
                  ✅ Øvrige oppgaver ({ovrige.length})
                </h4>
                <div className="todos-liste">
                  {ovrige.map((todo) => (
                    <div
                      key={todo.id}
                      className="todo-item"
                      onClick={() => setRedigerTodo(todo)}
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        onChange={() => markerFullfort(todo.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="todo-checkbox"
                      />
                      <div className="todo-content">
                        <h4>{todo.tittel}{getKritiskhetBadge(todo.kritiskhet)}</h4>
                        {todo.beskrivelse && <p className="todo-beskrivelse">{todo.beskrivelse}</p>}
                        <div className="todo-meta">
                          <span className="todo-dato">{formatDato(todo.forfall_dato)}</span>
                          {todo.sak_saksnummer && (
                            <span className="todo-sak">Sak #{todo.sak_saksnummer}</span>
                          )}
                        </div>
                      </div>
                      <span
                        className="todo-prioritet"
                        style={{ backgroundColor: getPrioritetFarge(todo.prioritet) }}
                        title={todo.prioritet}
                      >
                        {todo.prioritet === 'urgent' ? '🔥' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Rediger Todo Modal */}
      {redigerTodo && (
        <div className="modal-overlay" onClick={() => setRedigerTodo(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>✏️ Rediger gjøremål</h3>
            <form onSubmit={oppdaterTodo}>
              <input
                type="text"
                placeholder="Tittel..."
                value={redigerTodo.tittel}
                onChange={(e) => setRedigerTodo({ ...redigerTodo, tittel: e.target.value })}
                required
              />
              <textarea
                placeholder="Beskrivelse (valgfri)"
                value={redigerTodo.beskrivelse || ''}
                onChange={(e) => setRedigerTodo({ ...redigerTodo, beskrivelse: e.target.value })}
                rows={3}
              />
              <div className="todo-form-row">
                <label style={{display: 'block', marginBottom: '4px', fontSize: '0.9rem', color: '#666'}}>Sak (valgfritt)</label>
                <select
                  value={redigerTodo.sak_id || ''}
                  onChange={(e) => setRedigerTodo({ ...redigerTodo, sak_id: e.target.value || null })}
                  style={{width: '100%'}}
                >
                  <option value="">Ingen sak</option>
                  {saker.map(sak => (
                    <option key={sak.id} value={sak.id}>
                      {sak.saksnummer} - {sak.tittel || sak.klient_navn}
                    </option>
                  ))}
                </select>
              </div>
              <div className="todo-form-row">
                <select
                  value={redigerTodo.prioritet}
                  onChange={(e) => setRedigerTodo({ ...redigerTodo, prioritet: e.target.value })}
                >
                  <option value="lav">Lav prioritet</option>
                  <option value="middels">Middels prioritet</option>
                  <option value="høy">Høy prioritet</option>
                  <option value="urgent">🔥 Urgent</option>
                </select>
                <input
                  type="date"
                  value={redigerTodo.forfall_dato ? new Date(Number(redigerTodo.forfall_dato)).toISOString().split('T')[0] : ''}
                  onChange={(e) => setRedigerTodo({ ...redigerTodo, forfall_dato: e.target.value ? new Date(e.target.value).getTime() : null })}
                />
              </div>
              <div className="todo-form-row">
                <label style={{display: 'block', marginBottom: '4px', fontSize: '0.9rem', color: '#666'}}>Kritiskhetsnivå</label>
                <select
                  value={redigerTodo.kritiskhet || 'normal'}
                  onChange={(e) => setRedigerTodo({ ...redigerTodo, kritiskhet: e.target.value })}
                  style={{width: '100%'}}
                  title="Kritiskhetsnivå - viktig/absolutt gir timevarsler"
                >
                  <option value="normal">⚪ Normal kritiskhet</option>
                  <option value="viktig">🟡 Viktig (varsles hver time)</option>
                  <option value="absolutt">🔴 Absolutt (kritisk varsling)</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-lagre">Lagre endringer</button>
                <button type="button" className="btn-avbryt" onClick={() => setRedigerTodo(null)}>Avbryt</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
